import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { Link } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { getReturnOrderNumberString, getReturnOrderStateColor } from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import { NoteText } from "@/components/notes";
import { useReturnOrder } from "@/hooks/use-return-order";

/** Props Interface */
interface ReturnOrderInformationProps {
	returnOrderId: string;
}

/**
 * What the run is and who raised it — the facts that do not change once it is filed.
 *
 * The project rather than an order: a return is raised for a **site**, never against an outbound
 * order, because a truck comes off a job carrying material from months of deliveries and nobody
 * loading it knows which order a given pallet shipped on.
 */
export function ReturnOrderInformation({ returnOrderId }: ReturnOrderInformationProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { returnOrder } = useReturnOrder(returnOrderId);

	/** Render */
	return (
		<ContentCard
			title={t("general.information")}
			icon={icon("Info")}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("stock.return_order_no")}</DataGrid.Label>
					<DataGrid.Value>{getReturnOrderNumberString(returnOrder?.number)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general.state")}</DataGrid.Label>
					<DataGrid.Value>
						{returnOrder ? (
							<Tag
								variant="plain"
								color={getReturnOrderStateColor(returnOrder.state)}
							>
								{t(`stock.return_order_state.${returnOrder.state}`)}
							</Tag>
						) : (
							guardString(null)
						)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("projects.project")}</DataGrid.Label>
					<DataGrid.Value>
						{returnOrder ? (
							<Link href={`/projects/${returnOrder.projectId}`}>
								{guardString(returnOrder.projectName)}
							</Link>
						) : (
							guardString(null)
						)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("projects.fields.project_number")}</DataGrid.Label>
					<DataGrid.Value>{guardString(returnOrder?.projectNumber)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general_fields.created_by")}</DataGrid.Label>
					<DataGrid.Value>{guardString(returnOrder?.userName)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general_fields.created_at")}</DataGrid.Label>
					<DataGrid.Value>{moment(returnOrder?.createdAt).longFormat()}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("notes.note")}</DataGrid.Label>
					<DataGrid.Value>
						<NoteText note={guardString(returnOrder?.note)} />
					</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
		</ContentCard>
	);
}
